import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Lock, RefreshCw } from 'lucide-react';
import { Button, Chip, Select, TextField, Tooltip } from '@nyce/admin-kit/ui';
import type { DbServer, DbServerInput, DbServerTest, DiscoveredInstance, SslMode } from '../../api';
import { StatusBadge } from '../../components/status';
import { api } from '../../session';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import {
  EditorDialog,
  EnvChip,
  EnvField,
  IdField,
  SLUG,
  SwitchField,
  byName,
  errorText,
  useEditorForm,
} from '../../components/registry';

type Form = Required<Omit<DbServerInput, 'instance'>> & { id: string; instance: string };

const empty: Form = {
  id: '',
  name: '',
  host: '',
  port: 5432,
  username: '',
  password: '',
  sslMode: 'require',
  environment: 'production',
  protected: false,
  enabled: true,
  instance: '',
};

const toForm = ({ updatedAt: _u, hasPassword: _h, ...s }: DbServer): Form => ({
  ...s,
  instance: s.instance ?? '',
  password: '',
});

/** The PUT body: an empty password is left out so the server keeps the stored one; no instance = null. */
export function toDbServerInput({ id: _id, password, instance, ...rest }: Form): DbServerInput {
  const body = { ...rest, instance: instance || null };
  return password ? { ...body, password } : body;
}

/**
 * A new server for a discovered instance: id and name from the instance, its private address, production
 * instances protected (never a copy target) by default. Only user and password are left to type.
 */
export function formFromInstance(i: DiscoveredInstance): Form {
  const environment = /prod/.test(`${i.projectId} ${i.name}`) ? 'production' : 'staging';
  const id = i.name
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
  return {
    ...empty,
    id,
    name: i.name,
    host: i.privateIp ?? i.publicIp ?? '',
    environment,
    protected: environment === 'production',
    instance: i.resourceName,
  };
}

const KIND: Record<DiscoveredInstance['kind'], string> = { alloydb: 'AlloyDB', cloudsql: 'Cloud SQL' };

const SSL_OPTIONS: { value: SslMode; label: string }[] = [
  { value: 'require', label: 'require' },
  { value: 'prefer', label: 'prefer' },
  { value: 'disable', label: 'disable' },
];

function ServerForm({
  initial,
  isNew,
  hasPassword,
  instances,
  onSaved,
  onClose,
}: {
  initial: Form;
  isNew: boolean;
  hasPassword: boolean;
  /** Discovered Postgres instances not linked to another server. */
  instances: DiscoveredInstance[];
  onSaved: (s: DbServer) => void;
  onClose: () => void;
}) {
  const f = useEditorForm<Form>(initial, isNew);
  const { v, set } = f;
  const portOk = Number.isInteger(v.port) && v.port > 0 && v.port < 65536;
  const canSave = SLUG.test(v.id) && !!v.name.trim() && !!v.host.trim() && !!v.username.trim() && portOk;
  const save = () =>
    void f.run(async () => {
      onSaved(
        await api.admin.dbServers.put(
          v.id,
          toDbServerInput({ ...v, name: v.name.trim(), host: v.host.trim() })
        )
      );
    });
  return (
    <EditorDialog
      title={isNew ? 'New database server' : `Server ${v.id}`}
      error={f.error}
      busy={f.busy}
      canSave={canSave}
      onSave={save}
      onClose={onClose}
    >
      <TextField label="Name" value={v.name} onChange={(e) => set('name', e.target.value)} required />
      <Select
        label="GCP instance"
        value={v.instance}
        onChange={(r) => set('instance', r)}
        options={[
          { value: '', label: '— not linked —' },
          ...instances.map((i) => ({
            value: i.resourceName,
            label: `${i.name} (${KIND[i.kind]}, ${i.projectId})`,
          })),
          ...(v.instance && !instances.some((i) => i.resourceName === v.instance)
            ? [{ value: v.instance, label: v.instance }]
            : []),
        ]}
      />
      <IdField value={v.id} isNew={isNew} onChange={f.setId} example="alloydb-prod" />
      <TextField
        label="Host"
        value={v.host}
        onChange={(e) => set('host', e.target.value)}
        hint="private address reachable from the API pods"
        required
      />
      <TextField
        label="Port"
        type="number"
        value={String(v.port)}
        onChange={(e) => set('port', Number(e.target.value))}
        error={portOk ? undefined : '1–65535'}
      />
      <TextField
        label="Username"
        value={v.username}
        onChange={(e) => set('username', e.target.value)}
        required
      />
      <TextField
        label="Password"
        type="password"
        autoComplete="new-password"
        value={v.password}
        placeholder={hasPassword ? '•••••• stored' : ''}
        onChange={(e) => set('password', e.target.value)}
        hint={hasPassword ? 'leave empty to keep the stored password' : 'stored encrypted'}
      />
      <Select
        label="SSL mode"
        value={v.sslMode}
        onChange={(m) => set('sslMode', m as SslMode)}
        options={SSL_OPTIONS}
      />
      <EnvField value={v.environment} onChange={(e) => set('environment', e)} />
      <SwitchField
        label="Protected"
        checked={v.protected}
        onChange={(p) => set('protected', p)}
        hint="Never used as a copy/restore target"
      />
      <SwitchField label="Enabled" checked={v.enabled} onChange={(e) => set('enabled', e)} />
    </EditorDialog>
  );
}

function TestResult({ result }: { result: DbServerTest | 'busy' | undefined }) {
  if (!result) return null;
  if (result === 'busy') return <span className="nyk-ui-hint">testing…</span>;
  if (!result.ok)
    return (
      <Tooltip text={result.error || 'failed'}>
        <Chip size="sm" className="oph-outcome oph-outcome-error" data-test-result="error">
          failed
        </Chip>
      </Tooltip>
    );
  const tip = [result.version, result.databases != null ? `${result.databases} databases` : '']
    .filter(Boolean)
    .join(' · ');
  return (
    <Tooltip text={tip || 'connected'}>
      <Chip size="sm" className="oph-outcome oph-outcome-ok" data-test-result="ok">
        ok{result.latencyMs != null ? ` · ${Math.round(result.latencyMs)} ms` : ''}
      </Chip>
    </Tooltip>
  );
}

export function DbServersPage() {
  const list = useAsync(() => api.admin.dbServers.list(), []);
  const discovery = useAsync(() => api.admin.discovery(), []);
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState<{ value: Form; isNew: boolean; hasPassword: boolean } | null>(null);
  const [tested, setTested] = useState<Record<string, DbServerTest | 'busy'>>({});
  const notice = useNotice();
  const rows = [...(list.data ?? [])].sort(byName);

  const remove = async (s: DbServer) => {
    try {
      await api.admin.dbServers.remove(s.id);
      notice.success(`Deleted ${s.name || s.id}`);
      await list.reload();
    } catch (e) {
      notice.error(`Could not delete ${s.name || s.id}: ${errorText(e)}`);
    }
  };
  const test = async (s: DbServer) => {
    setTested((t) => ({ ...t, [s.id]: 'busy' }));
    try {
      const r = await api.admin.dbServers.test(s.id);
      setTested((t) => ({ ...t, [s.id]: r }));
    } catch (e) {
      setTested((t) => ({ ...t, [s.id]: { ok: false, error: errorText(e) } }));
    }
  };

  const linked = new Set((list.data ?? []).map((s) => s.instance).filter(Boolean));
  const postgres = (discovery.data?.instances ?? []).filter((i) => i.engine === 'postgres');
  const free = (keep: string) =>
    postgres.filter((i) => !linked.has(i.resourceName) || i.resourceName === keep);
  const addFor = (i: DiscoveredInstance) =>
    setEditing({ value: formFromInstance(i), isNew: true, hasPassword: false });

  // `?add=<instance>` (dashboard "Add credentials") opens the form for that instance once.
  const addParam = params.get('add');
  useEffect(() => {
    if (!addParam || !discovery.data || !list.data) return;
    const i = discovery.data.instances.find((x) => x.resourceName === addParam);
    if (i && !linked.has(i.resourceName)) addFor(i);
    setParams(
      (cur) => {
        const next = new URLSearchParams(cur);
        next.delete('add');
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addParam, discovery.data, list.data]);

  return (
    <Page
      title="DB servers"
      description="Credentials for the DB tools (sessions, tables, backups, copies). Instances are discovered in GCP; status and connections need no credentials."
      actions={
        <Button onClick={() => setEditing({ value: { ...empty }, isNew: true, hasPassword: false })}>
          Add manually
        </Button>
      }
    >
      <ErrorBox error={list.error} />
      <Loading loading={list.loading && !list.data} />
      {list.data && !rows.length && (
        <EmptyState
          card
          title="No credentials stored yet"
          hint="Pick a GCP instance below and add a user and password where you need the DB tools."
        />
      )}
      {!!rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="db-servers-table">
              <thead>
                <tr>
                  <th>Server</th>
                  <th>Environment</th>
                  <th>Address</th>
                  <th>User</th>
                  <th>Connection</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} data-row={s.id}>
                    <td className="nyk-card-title">
                      <b>{s.name || s.id}</b>
                      <div className="nyk-ui-hint">{s.id}</div>
                      <span className="nyk-inline">
                        {s.protected && (
                          <Chip size="sm" className="oph-lock" icon={<Lock size={11} />}>
                            protected
                          </Chip>
                        )}
                        {!s.enabled && (
                          <Chip size="sm" className="oph-off">
                            disabled
                          </Chip>
                        )}
                      </span>
                    </td>
                    <td data-label="Environment">
                      <EnvChip env={s.environment} />
                    </td>
                    <td data-label="Address">
                      <span className="nyk-url">
                        {s.host}:{s.port}
                      </span>
                      <div className="nyk-ui-hint">ssl {s.sslMode}</div>
                    </td>
                    <td data-label="User">
                      {s.username}
                      {!s.hasPassword && <div className="nyk-ui-hint">no password stored</div>}
                    </td>
                    <td data-label="Connection">
                      <TestResult result={tested[s.id]} />
                    </td>
                    <td className="nyk-row-actions">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void test(s)}
                        disabled={tested[s.id] === 'busy'}
                      >
                        Test
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setEditing({ value: toForm(s), isNew: false, hasPassword: s.hasPassword })
                        }
                      >
                        Edit
                      </Button>
                      <ConfirmButton
                        size="sm"
                        variant="danger"
                        message={`Delete server ${s.name || s.id}?`}
                        onConfirm={() => void remove(s)}
                      >
                        Delete
                      </ConfirmButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      <div className="nyk-card" data-testid="discovered-instances">
        <div className="nyk-card-head">
          <h2>GCP instances</h2>
          <span className="nyk-inline">
            {discovery.data && (
              <span className="nyk-ui-hint">
                {discovery.data.projects.length} projects ·{' '}
                {new Date(discovery.data.fetchedAt).toLocaleTimeString()}
              </span>
            )}
            <Button
              size="sm"
              variant="ghost"
              icon={<RefreshCw size={14} />}
              loading={discovery.loading}
              onClick={() => void discovery.reload()}
            >
              Rescan
            </Button>
          </span>
        </div>
        <ErrorBox error={discovery.error} />
        {discovery.data?.errors.map((e, i) => (
          <div key={i} className="oph-log-note">
            {e.projectId || 'projects'} · {e.api}: {e.message}
          </div>
        ))}
        {discovery.data && !discovery.data.instances.length && (
          <EmptyState
            title="No Cloud SQL or AlloyDB instances"
            hint="None in the projects the hub's identity can see."
          />
        )}
        {!!discovery.data?.instances.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards">
              <thead>
                <tr>
                  <th>Instance</th>
                  <th>State</th>
                  <th>Engine</th>
                  <th>Private IP</th>
                  <th>Credentials</th>
                </tr>
              </thead>
              <tbody>
                {discovery.data.instances.map((i) => {
                  const server = (list.data ?? []).find((s) => s.instance === i.resourceName);
                  return (
                    <tr key={i.resourceName} data-instance={i.resourceName}>
                      <td className="nyk-card-title">
                        <b>{i.name}</b>
                        <div className="nyk-ui-hint">
                          {[KIND[i.kind], i.role !== 'primary' && i.role, i.projectId, i.tier]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </td>
                      <td data-label="State">
                        <StatusBadge status={i.health} />
                      </td>
                      <td data-label="Engine">
                        {i.engine} {i.version}
                      </td>
                      <td data-label="Private IP" className="nyk-mono">
                        {i.privateIp ?? '—'}
                      </td>
                      <td data-label="Credentials">
                        {server ? (
                          <span>{server.name}</span>
                        ) : i.engine !== 'postgres' ? (
                          <span className="nyk-ui-hint">not Postgres</span>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => addFor(i)}>
                            Add credentials
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroller>
        )}
      </div>
      {editing && (
        <ServerForm
          instances={free(editing.value.instance)}
          initial={editing.value}
          isNew={editing.isNew}
          hasPassword={editing.hasPassword}
          onClose={() => setEditing(null)}
          onSaved={(s) => {
            setEditing(null);
            notice.success(`Saved ${s.name || s.id}`);
            void list.reload();
          }}
        />
      )}
      {notice.element}
    </Page>
  );
}
